import { memo } from "react"
import { useNavigate } from "@tanstack/react-router"
import { IconMessagesOutline18 as ThreadIcon } from "nucleo-ui-outline-18"

import {
	Button,
	DropdownMenu,
	DropdownMenuItem,
	IconButton,
	LinearIcon,
} from "@productlane/ui"
import { EllipsisVerticalIcon as MoreVertical } from "@productlane/ui/src/icons/project/ellipsis-icon"
import { GitHubIcon } from "@productlane/ui/src/icons/project/github-icon"

import type { AgentCodeFixItem, CodeFixLinks } from "./agent-items"
import { api } from "@/lib/api"
import { useRole } from "@/lib/useRole"
import {
	DiffFiles,
	DiffStats,
	HarnessBadges,
	useBugInspectReview,
} from "../threads/components/center-content/timeline-events/bug-inspect-review-card"
import { codeFixLinks, codeFixTitle } from "./agent-items"
import { AgentMessage } from "./agent-message-header"
import { DecisionBar } from "./decision-bar"
import {
	CODE_FIX_INTRO,
	CODE_FIX_REPORT_LABEL,
	RUNNING_FIX_TEXT,
} from "./format"
import { IDEA_EXAMPLES_COPY } from "./idea-examples-model"
import { ThreadQuoteRow } from "./thread-quote-row"

const openExternal = (url: string) =>
	window.open(url, "_blank", "noopener,noreferrer")

const LinksMenu = memo(({ links }: { links: CodeFixLinks }) => {
	const navigate = useNavigate()
	if (!links.pullRequest && !links.issue && !links.thread) return null
	return (
		<DropdownMenu
			trigger={
				<IconButton
					Icon={MoreVertical}
					variant="ghost"
					iconClassName="fill-current"
					aria-label="More"
				/>
			}
			contentProps={{ side: "bottom", align: "start" }}
		>
			{links.pullRequest && (
				<DropdownMenuItem
					Icon={GitHubIcon}
					onSelect={() => openExternal(links.pullRequest!)}
				>
					Open in GitHub
				</DropdownMenuItem>
			)}
			{links.issue && (
				<DropdownMenuItem
					Icon={LinearIcon}
					onSelect={() => openExternal(links.issue!)}
				>
					Open in Linear
				</DropdownMenuItem>
			)}
			{links.thread && (
				<DropdownMenuItem
					Icon={ThreadIcon}
					onSelect={() => void navigate({ href: links.thread! })}
				>
					Open thread
				</DropdownMenuItem>
			)}
		</DropdownMenu>
	)
})

const SECTION_LABEL = "text-xs font-medium text-tertiary"

// A code fix as the agent's first message: what it fixed, the report behind it,
// the diff, and the review inside the card, like an idea.
export const CodeFixMessage = memo(
	({ item, onClosed }: { item: AgentCodeFixItem; onClosed: () => void }) => {
		const { fix, state } = item
		const { role } = useRole()
		const canAct = role !== "VIEWER"
		const reviewing = state === "review"
		const runId = fix.id

		const runQuery = api.bugInspect.run.useQuery(
			{ runId },
			{ enabled: reviewing },
		)
		const diffQuery = api.bugInspect.diff.useQuery(
			{ runId },
			{ enabled: reviewing, staleTime: 60_000 },
		)
		const { approve, reject, busy } = useBugInspectReview(runId, {
			onReviewed: onClosed,
		})

		const links = codeFixLinks(fix)
		const threadTitle = fix.feedback?.title?.trim()

		return (
			<div data-testid="agent-code-message" className="px-1 pb-6">
				<AgentMessage at={fix.statusChangedAt}>
					<div className="flex min-w-0 flex-col gap-4 pt-1">
						<div className="flex flex-col gap-3 text-sm leading-6 text-primary">
							<p>{reviewing ? CODE_FIX_INTRO : RUNNING_FIX_TEXT}</p>
							<p>{codeFixTitle(fix)}</p>
						</div>
						<div className="flex flex-col gap-5 rounded-xl border border-gray-200 bg-white p-5 dark:border-gray-700/80 dark:bg-gray-800">
							{reviewing && runQuery.data && (
								<HarnessBadges card={runQuery.data} size="default" />
							)}
							{links.thread && threadTitle && (
								<ThreadQuoteRow
									to={links.thread}
									quote={threadTitle}
									label={CODE_FIX_REPORT_LABEL}
								/>
							)}
							{reviewing && (
								<>
									<div className="flex items-center justify-between gap-2 border-t border-gray-200 pt-4 dark:border-gray-700/80">
										<span className={SECTION_LABEL}>
											{IDEA_EXAMPLES_COPY.change}
										</span>
										<div className="flex min-w-0 items-center gap-2 text-xs text-tertiary">
											<span className="truncate">{fix.repository}</span>
											{diffQuery.data && (
												<DiffStats files={diffQuery.data.files} />
											)}
											<LinksMenu links={links} />
										</div>
									</div>
									<DiffFiles runId={runId} defaultExpanded />
								</>
							)}
							{canAct && reviewing && (
								<DecisionBar>
									<Button
										variant="ghost"
										isLoading={reject.isLoading}
										disabled={busy}
										onClick={() => reject.mutate({ runId })}
									>
										Decline
									</Button>
									<Button
										isLoading={approve.isLoading}
										disabled={busy}
										onClick={() => approve.mutate({ runId })}
									>
										Accept
									</Button>
								</DecisionBar>
							)}
						</div>
					</div>
				</AgentMessage>
			</div>
		)
	},
)
